import { useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useBlocker } from 'react-router'
import { useBeforeUnload } from 'react-use'

import { Suspense } from '~/components/elements/Suspense'
import { checkCurrentBrandAccount } from '~/hoc/checkCurrentBrandAccount'
import { useLogEventOnMount } from '~/hooks/useLogEventOnMount'

import { commentAtom, savedCommentAtom } from './atoms'
import { TimeSeriesAnalysis } from './components/TimeSeriesAnalysis'
import { useMainQuery } from './hooks/useMainQuery'
import { useResetOnUnmount } from './hooks/useResetOnUnmount'

export const TimeSeriesAnalysisPage = checkCurrentBrandAccount(() => {
  const { t } = useTranslation()
  const { isLoading } = useMainQuery()
  const comment = useAtomValue(commentAtom)
  const savedComment = useAtomValue(savedCommentAtom)
  const allKeys = new Set([
    ...Object.keys(comment),
    ...Object.keys(savedComment),
  ])
  const isDirty = [...allKeys].some(
    (k) => (comment[k] ?? '') !== (savedComment[k] ?? ''),
  )
  const dirtyMessage = t(
    'edited_content_has_not_been_saved_do_you_still_want_to_leave',
  )

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname,
  )

  useBeforeUnload(isDirty, dirtyMessage)
  useLogEventOnMount('Access to time series analysis function')
  useResetOnUnmount()

  React.useEffect(() => {
    if (blocker.state === 'blocked') {
      const isConfirmed = window.confirm(dirtyMessage)
      if (isConfirmed) {
        blocker.proceed()
      } else {
        blocker.reset()
      }
    }
  }, [blocker, dirtyMessage])

  return (
    <Suspense loading={isLoading}>
      <TimeSeriesAnalysis />
    </Suspense>
  )
})
